import {
  getNotificationBus,
  type NotificationBus,
} from '@pogokid/dblite'
import {
  CommittedEvent,
  DomainEventRef,
  EventSourcingDatabase,
} from './types'
import { Kysely, Transaction, sql } from '@pogokid/dblite/kysely'
import { DomainEvent } from './DomainEvent'

export class EventStore extends EventTarget {
  private notificationBus: NotificationBus

  constructor(private db: Kysely<EventSourcingDatabase>) {
    super()
    this.notificationBus = getNotificationBus()
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static new(db: Kysely<any>): EventStore {
    return new EventStore(db)
  }

  async append(domainEvent: DomainEvent): Promise<DomainEvent> {
    await this.db
      .insertInto('domain_event')
      .values({
        ...domainEvent.intoSerialisedWithoutSequences(),
        device_sequence_number: sql<number>`(SELECT IFNULL(MAX(device_sequence_number), 0) + 1 FROM domain_event)`,
      })
      .returningAll()
      .executeTakeFirstOrThrow()

    this.notifySubscribers(domainEvent)
    this.notifyAppliedSubscribers()

    return domainEvent
  }

  /**
   * Append a batch of events that should be grouped by
   * a correlation_id
   *
   * This will generate a unique correlation_id and set
   * it on each event before applying it
   *
   * @param events
   */
  async appendBatch(
    events: DomainEvent[]
  ): Promise<DomainEvent[]> {
    const correlationId =
      events.length > 0 ? crypto.randomUUID() : null
    const domainEvents: DomainEvent[] = []
    for (const event of events) {
      const result = await this.db
        .insertInto('domain_event')
        .values({
          ...event
            .setCorrelationId(correlationId)
            .intoSerialisedWithoutSequences(),
          device_sequence_number: sql<number>`(SELECT IFNULL(MAX(device_sequence_number), 0) + 1 FROM domain_event)`,
        })
        .returningAll()
        .executeTakeFirst()

      if (result) {
        domainEvents.push(DomainEvent.fromData(result))
      }
    }

    // Notify subscribers for each event
    domainEvents.forEach((event) =>
      this.notifySubscribers(event)
    )

    this.notifyAppliedSubscribers()

    return domainEvents
  }

  async mergePulledEvents(events: DomainEvent[]): Promise<void> {
    await this.db.transaction().execute(async (trx) => {
      for await (const event of events) {
        // Check if event already exists (by id)
        const existing = await trx
          .selectFrom('domain_event')
          .selectAll()
          .where('id', '=', event.id)
          .executeTakeFirst()

        if (!existing) {
          await trx
            .insertInto('domain_event')
            .values(() => ({
              ...event.intoSerialised(),
              device_sequence_number: sql<number>`(SELECT IFNULL(MAX(device_sequence_number), 0) + 1 FROM domain_event)`,
            }))
            .execute()
        }
      }
    })
    this.notifyAppliedSubscribers()
  }

  async getAllRefs(): Promise<DomainEventRef[]> {
    return this.db
      .selectFrom('domain_event_refs')
      .selectAll()
      .execute()
  }

  /**
   * Every profile this device holds events for.
   *
   * A profile that has signed in but never synced has events
   * without a sync ref, so this answers for them where
   * {@link getAllRefs} cannot.
   */
  async getEventProfileIds(): Promise<string[]> {
    const rows = await this.db
      .selectFrom('domain_event')
      .select('profile_id')
      .distinct()
      .execute()
    return rows.map((row) => row.profile_id)
  }

  async getEventsFromCursor<Txn extends EventSourcingDatabase>(
    txn: Transaction<Txn>,
    aggregateType: string,
    profileId: string,
    fromSequence: number,
    limit: number
  ): Promise<DomainEvent[]> {
    const query = (
      txn as unknown as Transaction<EventSourcingDatabase>
    )
      .selectFrom('domain_event')
      .selectAll()
      .where('aggregate_type', '=', aggregateType)
      .where('profile_id', '=', profileId)
      .where('device_sequence_number', '>', fromSequence)
      .orderBy('device_sequence_number', 'asc')
      .limit(limit)

    const results = await query.execute()
    return results.map(DomainEvent.fromData)
  }

  async getEventsByAggregate(
    aggregateType: string,
    fromSequence?: number
  ): Promise<DomainEvent[]> {
    let query = this.db
      .selectFrom('domain_event')
      .selectAll()
      .where('aggregate_type', '=', aggregateType)
      .orderBy('device_sequence_number', 'asc')

    if (fromSequence !== undefined) {
      query = query.where(
        'device_sequence_number',
        '>=',
        fromSequence
      )
    }

    const results = await query.execute()
    return results.map(DomainEvent.fromData)
  }

  getEventsByTypeQuery(
    aggregateType: string,
    profileId: string,
    eventType: string,
    limit?: number
  ) {
    let query = this.db
      .selectFrom('domain_event')
      .selectAll()
      .where('aggregate_type', '=', aggregateType)
      .where('profile_id', '=', profileId)
      .where('event_type', '=', eventType)
      .orderBy('sequence_number', 'desc')

    if (limit) {
      query = query.limit(limit)
    }

    return query
  }

  async getEventsByType(
    aggregateType: string,
    profileId: string,
    eventType: string,
    limit?: number
  ): Promise<DomainEvent[]> {
    const query = this.getEventsByTypeQuery(
      aggregateType,
      profileId,
      eventType,
      limit
    )

    const results = await query.execute()
    return results.map(DomainEvent.fromData)
  }

  async getEventsSince(timestamp: Date): Promise<DomainEvent[]> {
    const results = await this.db
      .selectFrom('domain_event')
      .selectAll()
      .where('timestamp', '>=', timestamp.toISOString())
      .orderBy('sequence_number', 'asc')
      .execute()

    return results.map(DomainEvent.fromData)
  }

  async getAllEvents(
    limit?: number,
    offset?: number
  ): Promise<DomainEvent[]> {
    let query = this.db
      .selectFrom('domain_event')
      .selectAll()
      .orderBy('sequence_number', 'asc')

    if (limit) {
      query = query.limit(limit)
    }

    if (offset) {
      query = query.offset(offset)
    }

    const results = await query.execute()
    return results.map(DomainEvent.fromData)
  }

  async getLatestEvent(
    aggregateType: string,
    profileId: string,
    sequence: 'device' | 'sync'
  ): Promise<DomainEvent | null> {
    let query = this.db
      .selectFrom('domain_event')
      .selectAll()
      .where('aggregate_type', '=', aggregateType)
      .where('profile_id', '=', profileId)

    if (sequence === 'sync') {
      query = query.orderBy('sequence_number', 'desc')
    } else {
      query = query.orderBy('device_sequence_number', 'desc')
    }

    const latestEventResult = await query
      .limit(1)
      .executeTakeFirstOrThrow()

    return latestEventResult
      ? DomainEvent.fromData(latestEventResult)
      : null
  }

  async getPendingEvents(
    aggregateType: string,
    profileId: string
  ): Promise<DomainEvent[]> {
    const results = await this.db
      .selectFrom('domain_event')
      .selectAll()
      .where('aggregate_type', '=', aggregateType)
      .where('profile_id', '=', profileId)
      .where('sequence_number', 'is', null) // Not synced yet
      .orderBy('device_sequence_number', 'asc')
      .execute()

    return results.map(DomainEvent.fromData)
  }

  /**
   * How many events this device has created that the server has
   * not accepted yet
   */
  async countPendingEvents(
    aggregateType: string,
    profileId: string
  ): Promise<number> {
    const result = await this.db
      .selectFrom('domain_event')
      .select((eb) => eb.fn.countAll<number>().as('pending'))
      .where('aggregate_type', '=', aggregateType)
      .where('profile_id', '=', profileId)
      .where('sequence_number', 'is', null) // Not synced yet
      .executeTakeFirst()

    return Number(result?.pending ?? 0)
  }

  /**
   * How many events these profiles have created that the server
   * has not accepted yet, across every aggregate type
   */
  async countPendingEventsForProfiles(
    profileIds: string[]
  ): Promise<number> {
    if (profileIds.length === 0) return 0
    const result = await this.db
      .selectFrom('domain_event')
      .select((eb) => eb.fn.countAll<number>().as('pending'))
      .where('profile_id', 'in', profileIds)
      .where('sequence_number', 'is', null) // Not synced yet
      .executeTakeFirst()

    return Number(result?.pending ?? 0)
  }

  async markEventsSynced(
    eventIds: CommittedEvent[]
  ): Promise<number | undefined> {
    if (eventIds.length === 0) return
    let lastUpdatedSequenceNumber: number | undefined = undefined
    await this.db.transaction().execute(async (trx) => {
      for await (const event of eventIds) {
        const { numUpdatedRows } = await trx
          .updateTable('domain_event')
          .set({
            sequence_number: event.sequence_number,
            created_at: event.created_at,
          })
          .where('id', '=', event.id)
          .executeTakeFirst()

        // We only track updates to events we know about
        if (
          !lastUpdatedSequenceNumber ||
          (event.sequence_number > lastUpdatedSequenceNumber &&
            numUpdatedRows > 0)
        ) {
          lastUpdatedSequenceNumber = event.sequence_number
        }
      }
    })
    return lastUpdatedSequenceNumber
  }

  private notifySubscribers(event: DomainEvent): void {
    // Notify global subscribers
    this.dispatchEvent(
      new CustomEvent('append', { detail: event })
    )

    // Notify aggregate-specific subscribers
    this.dispatchEvent(
      new CustomEvent(event.aggregate_type, {
        detail: event,
      })
    )
  }

  private notifyAppliedSubscribers(): void {
    this.notificationBus.notify('event_store', 'applied')
  }
}
